import { useEffect, useState } from 'react';
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import { data_fetch_api_resource_with_params } from "../../../utils/http_functions";
import { dayjs } from "../../../config";

import { regional_settings } from "../../../constants"

import { CircularProgress, Grid, useMediaQuery, useTheme } from "@mui/material"

import { BarChart, AreaChart } from "../../Chart/v2";
import DateSelector from "../DateSelector";
import { useSettingsContext } from '@/components/Settings/hooks/useSettingsContext';

const defaults = {
  type: 'monthly',
  startDate: dayjs().month(dayjs().month()-12).date(1).startOf('day').hour(1),
  endDate: dayjs().date(1).startOf('day').startOf('hour')
};

const CCHChart = ({
  contractId,
  tariff,
  isPeninsula=true,
  chartComponents={},
  setChartComponents
}) => {
  const Settings = useSettingsContext();
  const { t } = useTranslation();
  const token = useSelector((state) => state.auth.token);

  const [loading, setLoading] = useState(true);
  const [chartData, setChartData] = useState(null);
  const [dates, setDates] = useState({...defaults});

  const theme = useTheme();
  const breakpointDownMd = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    fetchChartSpec();
  }, [dates, contractId]);

  const handleDatesChange = (newDates) => {
    setDates(newDates);
  }

  const fetchChartSpec = () => {
    const translations = {
      'activa': t('common:text.contract_cch_active'),
      'exportada': t('common:text.contract_cch_exported'),
      'reactiva': t('common:text.contract_cch_reactive')
    }
    
    const spec_url = `contracts/${contractId}/cch/chart/${dates.type}/` +
      `?start=${dayjs(dates.startDate).utc(true).toISOString()}` + 
      `&end=${dayjs(dates.endDate).utc(true).toISOString()}`;

    setLoading(true);
    data_fetch_api_resource_with_params(token, spec_url, {translations}, 2)
      .then((response) => {
        if (response.status === 200 && response.data) {
          setChartData(response.data);
        } else {
          setChartData(null);
        }
      }).finally(() => {
        setLoading(false);
      });
  }

  useEffect(() => {
    setChartComponents(getComponents());
  }, [chartData])

  const getComponents = () => {
    let components = {};

    let tariffText = '_domestic';
    let zonaText = '';

    if (tariff && !tariff.includes('2.0')) {
      tariffText = '_industrial';
      zonaText = "_peninsula";
      if (!isPeninsula) {
        zonaText = "_balears";
      }
    }

    chartData?.magnitudes?.elements.map((el, idx) => {
      components[el] = {
        title: el,
        label: t(`common:chartLegend.${el}${tariffText}${zonaText}`),
      }
    })
    return components
  }

  return (
    <>
      <DateSelector 
        onDatesChange={handleDatesChange} 
        contractId={contractId} 
        defaults={defaults} 
        loading={loading} 
        endpointVersion={2}
      />

      <Grid container justifyContent="center" style={{ paddingTop: 20 }}>
        {loading &&
          <Grid item xs={12} align="center">
            <CircularProgress />
          </Grid>
        }
        {!loading &&
          <Grid item xs={12}>
            {chartData && <>
              {['hourly', 'quarter-hourly'].includes(dates.type) &&
                <AreaChart
                  data={chartData}
                  unit={regional_settings.energy_unit}
                  colorsOffset={3}
                  showTooltipInfo={true}
                />
              }
              {['monthly', 'daily'].includes(dates.type) &&
                <BarChart
                  data={chartData}
                  components={chartComponents}
                  unit={regional_settings.energy_unit}
                  enableLegendTooltip={!breakpointDownMd && Settings?.contract?.enableInfoCCH}
                />
              }
            </>}
            {!chartData &&
              <div style={{ textAlign: "center" }}>{t('common:text.contract_cch_chart_no_data')}</div>
            }
          </Grid>
        }
      </Grid>
    </>
  )
}

export default CCHChart;
